import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './NotificationDropdown.scss';
import { FaBell, FaRegBell, FaTimes, FaCopy, FaClock } from "react-icons/fa";
import { API_ENDPOINT_MARK_NOTIFICATIONS_AS_READ, API_ENDPOINT_MARK_SYSTEM_NOTIFICATIONS_AS_READ, API_ENDPOINT_NOTIFICATIONS } from '../../../data/Endpoints-API';
import { useAuthContext } from '../../../contexts/Auth-Context';
import { useChartContext } from '../../../contexts/Chart-Context';
import APIMiddleware from '../../../data/api/Api-Middleware';
import { adjustDateTime, formatDate } from '../../../utils/format';
import { useMetricsContext } from '../../../contexts/Metrics-Context';
import { Ripple } from 'react-ripple-click';
import SystemNotification from '../order_panel/SystemNotification';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { localizeNotificationMessage } from '../../../i18n/notificationMessage.js';

const NotificationDropdown = () => {
    const { t } = useTranslation();
    const {
        user,
        setSystemNotificationCount,
        setAlertNotificationCount,
      } = useAuthContext();
      const { utcOffset, metrics } =  useMetricsContext();
    const { selectedStyle } = useChartContext();

    const [isOpen, setIsOpen] = useState(false);
    const [notifications , setNotifications ] = useState([]);
    const [isAlertLoading, setIsAlertLoading] = useState(false);
    const [visibleCount,setVisibleCount] = useState(15);
    const [isNotificationsVisible, setIsNotificationsVisible] = useState(true);
    const [isSystemNotificationsVisible, setIsSystemNotificationsVisible] = useState(false);


    const dropdownRef = useRef(null);
    const [panelBounds, setPanelBounds] = useState(null);
    useLayoutEffect(() => {
      if (!isOpen) return;
      const panel = dropdownRef.current?.closest('.right-nav');
      if (!panel) return;
      const measure = () => {
        const rect = panel.getBoundingClientRect();
        const icon = dropdownRef.current.querySelector('.notification-icon').getBoundingClientRect();
        setPanelBounds({ left: rect.left + 8, width: Math.max(0, rect.width - 16), top: icon.bottom + 8 });
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(panel);
      window.addEventListener('resize', measure);
      return () => { observer.disconnect(); window.removeEventListener('resize', measure); };
    }, [isOpen]);

    const alertUnseenCount = metrics.userUnseenNotificationsCount || 0;
    const systemUnseenCount = metrics.userUnseenSystemNotificationsCount || 0;
    const totalUnseenCount = alertUnseenCount + systemUnseenCount;

    const markNotifictionsAsRead = async () => {
        try {
          if (user && user.userId !== undefined && user.userId > 0) {
            await APIMiddleware.get(
              API_ENDPOINT_MARK_NOTIFICATIONS_AS_READ(user.userId),
            );
            setAlertNotificationCount(0);
          }
        } catch (error) {
          // Handle API request error
          console.error(`API request error: ${API_ENDPOINT_MARK_NOTIFICATIONS_AS_READ}`, error);
        }
      };

    const markSystemNotificationsAsRead = async () => {
        try {
          if (user && user.userId !== undefined && user.userId > 0) {
            await APIMiddleware.get(
              API_ENDPOINT_MARK_SYSTEM_NOTIFICATIONS_AS_READ(user.userId),
            );
            setSystemNotificationCount(0);
          }
        } catch (error) {
          console.error(`API request error: ${API_ENDPOINT_MARK_SYSTEM_NOTIFICATIONS_AS_READ}`, error);
        }
      };

    const closeDropdown = async () => {
        if (isOpen) {
          if (isSystemNotificationsVisible) {
            await markSystemNotificationsAsRead();
          } else {
            await markNotifictionsAsRead();
          }
        }
        setIsOpen(false);
    };

    const toggleDropdown = async () => {
        if (!isOpen) {
          setIsOpen(true);
          setIsAlertLoading(true);
          setAlertNotificationCount(0);
          await getUserNotifications();
          return;
        }
        await closeDropdown();
    } 


    const getUserNotifications = async () => {
        setIsAlertLoading(true);
        try {
          if (user && user.userId !== undefined && user.userId > 0) {
            const response = await APIMiddleware.get(
              API_ENDPOINT_NOTIFICATIONS(user.userId),
            );
            const responseData = response.data;
            setNotifications(Array.isArray(responseData) ? responseData : []);
          } else {
            setNotifications([]);
          }
        } catch (error) {
          setNotifications([]);
          console.error(`API request error: ${API_ENDPOINT_NOTIFICATIONS}`, error);
        } finally {
          setIsAlertLoading(false);
        }
      };

      // close notification when click outside the notfication bar
      useEffect(() => {
        const handleClickOutside = (event) => {             
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {                 
              closeDropdown();             
            }         
        };         
        // Attach event listener
        document.addEventListener('mousedown', handleClickOutside);         
        
        // Clean up event listener on component unmount
        return () => {             
            document.removeEventListener('mousedown', handleClickOutside);         
        };     
      }, []);

      const loadMoreNotification = ()=>
      {
        setVisibleCount((prevNotification)=> prevNotification + 15);
      }
      const toggleSystemNotificationsVisibility = async () => {
        setIsSystemNotificationsVisible(true);
        setIsNotificationsVisible(false);
        await markSystemNotificationsAsRead();
      };
    
      const toggleAlertNotificationsVisibility = async () => {
        if (isNotificationsVisible) return;

        setIsNotificationsVisible(true);
        setIsSystemNotificationsVisible(false);
        setNotifications([]);
        setVisibleCount(15);
        setIsAlertLoading(true);

        await getUserNotifications();
        await markNotifictionsAsRead();
      };
    
    const stripHtml = (html) => {
      if (typeof window !== "undefined") {
        const doc = new DOMParser().parseFromString(html, "text/html");
        return doc.body.textContent || "";
      }

      // Replace tags with a new line before the tag
      return html.replace(/<\/?[^>]+(>|$)/g, (match) => {
        return '\n' + match;
      }).replace(/<\/?[^>]+(>|$)/g, ""); // Remove the tags
    };
    const copyToClipboard = async (htmlString) => {
      const tempElement = document.createElement("div");
      tempElement.innerHTML = htmlString;
  
      const textToCopy = tempElement.innerText || tempElement.textContent;
  
      try {
          await navigator.clipboard.writeText(textToCopy);
          toast.success(t('toasts.copiedDetails'), { position: "top-right" });
      } catch (err) {
          console.error("Failed to copy text: ", err);
      }
  };
  
    return (
      <>
        <style>
        {`
        .heading {
          padding: 0 !important;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          width: 100%;
          flex-shrink: 0;
          background: #252b42;
          gap: 0;
          border-bottom: none !important;
        }
        .notification-header-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding: 12px 14px 8px !important;
          box-sizing: border-box;
        }
        .notification-title {
          margin: 0 !important;
          padding: 0 !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          letter-spacing: 0.2px !important;
          color: #e8eaf0 !important;
          line-height: 1.2 !important;
        }
        .heading .symbol-mode-tabs {
          display: flex !important;
          flex-direction: row !important;
          align-items: stretch !important;
          justify-content: stretch !important;
          width: 100% !important;
          margin-bottom: 0 !important;
          gap: 0 !important;
          border-top: 1px solid #3a4156 !important;
          border-bottom: 1px solid #3a4156 !important;
          background: #252b42 !important;
        }
        .heading .symbol-mode-tabs .mode-tab-button {
          flex: 1 1 50% !important;
          width: auto !important;
          min-width: 0 !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0.5rem 0.25rem !important;
          border: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          background-color: #252b42 !important;
          color: #b8bcc8 !important;
          font-size: 10px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
        }
        .heading .symbol-mode-tabs .mode-tab-button.mode-tab-active {
          background-color: var(--buy-color, #21C46D) !important;
          color: #1a1f36 !important;
          box-shadow: none !important;
        }
        .tab-notification-count {
          margin-left: 6px;
          background-color: #ff4757;
          color: #e8eaf0;
          border-radius: 999px;
          padding: 1px 5px;
          font-size: 9px;
          line-height: 1.3;
          min-width: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        body[class] .right-nav .mode-tabs .notification-dropdown .heading .close-icon,
        html body .notification-dropdown .heading .close-icon {
          padding: 0.1rem 0.3rem !important;
          margin: 0 !important;
          width: auto !important;
          height: auto !important;
          min-width: 0 !important;
          max-width: none !important;
          border: none !important;
          border-radius: 2px !important;
          box-shadow: none !important;
          background-color: transparent !important;
          font-size: 11px !important;
          line-height: normal !important;
        }
        body[class] .right-nav .mode-tabs .notification-dropdown .heading .close-icon:hover:not(:disabled),
        html body .notification-dropdown .heading .close-icon:hover:not(:disabled) {
          background-color: var(--sell-color) !important;
        }
        `}
      </style>
        {isOpen && (
          <div className="notification-backdrop" onClick={closeDropdown}></div>
        )}
        <div className="notification-dropdown" ref={dropdownRef}>
            <div
              role="button"
              tabIndex={0}
              onClick={toggleDropdown}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleDropdown();
                }
              }}
              className={`notification-icon${isOpen ? ' is-open' : ''}`}
              aria-expanded={isOpen}
              aria-label={t('notifications.title')}
            >
                {totalUnseenCount > 0 ? <FaBell size={14} /> : <FaRegBell size={14} />}
                {totalUnseenCount > 0 && <span className="notification-count">{totalUnseenCount}</span>}
            </div>
            {isOpen && (
                <div className={"notification-list" + (panelBounds ? " panel-contained" : "")} style={panelBounds ? { "--notification-left": panelBounds.left + "px", "--notification-width": panelBounds.width + "px", "--notification-top": panelBounds.top + "px" } : undefined}>
                    <div className="heading heading1">
                          <div className="notification-header-bar">
                            <h2 className="notification-title">{t('notifications.title')}</h2>
                            <button
                              onClick={closeDropdown}
                              className="close-icon"
                              aria-label={t('notifications.closeNotifications')}
                            >
                              <FaTimes />
                            </button>
                          </div>
                          <div className="symbol-mode-tabs">
                            <button
                                type="button"
                                onClick={toggleAlertNotificationsVisibility}
                                className={`mode-tab-button ${isNotificationsVisible ? 'mode-tab-active' : ''}`}
                                style={{
                                  position: 'relative',
                                  overflow: 'hidden',
                                  isolation: 'isolate',
                                }}
                              >
                                <Ripple />
                                {t('notifications.alertTab')}
                                {alertUnseenCount > 0 && (
                                  <span className="tab-notification-count">{alertUnseenCount}</span>
                                )}
                              </button>

                              <button
                                type="button"
                                onClick={toggleSystemNotificationsVisibility}
                                className={`mode-tab-button ${isSystemNotificationsVisible ? 'mode-tab-active' : ''}`}
                                style={{
                                  position: 'relative',
                                  overflow: 'hidden',
                                  isolation: 'isolate',
                                }}
                              >
                                <Ripple />
                                {t('notifications.systemTab')}
                                {systemUnseenCount > 0 && (
                                  <span className="tab-notification-count">{systemUnseenCount}</span>
                                )}
                              </button>
                          </div>
                    </div>
                    {/* <div className="notification-list-item">
                      {notifications.length === 0 ? (
                          <div className="no-notifications">{t('common.noNotificationsFound')}</div>
                        ) : (
                          notifications.slice(0 , visibleCount).map((notification, index) => (
                            <div key={index} className="notification-item">
                              <div className="message">{notification.message}</div>
                              <div className="date">
                                {formatDate(adjustDateTime(notification.created_at, utcOffset))}
                              </div>
                            </div>
                          ))
                        )}
                    </div> */}
                    {/* {
                      visibleCount < notifications.length && (
                        <button className='load-more' onClick={loadMoreNotification}>
                          View More
                        </button>
                      )
                    } */}
                          {/* Notification Section */}
                    {/* Show regular notifications if isNotificationsVisible is true */}
                    {isNotificationsVisible && (
        <div className="notification-list-item">
          {isAlertLoading ? (
            <div className="loading">
              <span>{t('notifications.loading')}</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="no-notifications">
              <div style={{ fontSize: '14px', color: '#8b92b0' }}>{t('notifications.noAlertNotifications')}</div>
            </div>
          ) : (
            notifications.slice(0, visibleCount).map((notification, index) => (
              <div 
                key={notification.id || index} 
                className="notification-item"
              >
                <div className="notification-content-wrapper">
                  <div className="notification-text">
                    {notification.type === "transactionLog" ? (
                      stripHtml(notification.message).split("\n").filter(line => line.trim()).map((line, i) => (
                        <p key={i}>{localizeNotificationMessage(line.trim(), t)}</p>
                      ))
                    ) : (
                      <p>{localizeNotificationMessage(notification.message, t)}</p>
                    )}
                  </div>
                  <div className="notification-meta">
                    <div className="notification-time" style={{color: selectedStyle.buyColor}}>
                      <FaClock size={12} />
                      <span>{formatDate(adjustDateTime(notification.created_at, utcOffset))}</span>
                    </div>
                  </div>
                </div>
                {notification.type === "transactionLog" && (
                  <button 
                    className="notification-copy-btn" 
                    type="button"
                    onClick={() => copyToClipboard(notification.message)}
                    aria-label={t('notifications.copyNotification')}
                  >
                    <FaCopy />
                    <span>{t('common.copy')}</span>
                  </button>
                )}
              </div>
            ))
          )}

          {visibleCount < notifications.length && (
            <button type="button" className="load-more" onClick={loadMoreNotification}>
              View More
            </button>
          )}
        </div>
      )}

      {/* Show system notifications if isSystemNotificationsVisible is true */}
      {isSystemNotificationsVisible && <SystemNotification key="system-notifications" />}
                </div>
            )}
        </div>
        </>
    );
};

export default NotificationDropdown;
